// ICOPE评估系统 - 数据验证和状态处理工具函数
// 修复文档 assessment-and-content-rules.md 中识别的逻辑漏洞

export type AssessmentStatus = 'PASS' | 'FAIL' | 'OK' | 'NEEDS_ATTENTION' | 'SUPPORTED' | 'ADEQUATE' | 'INADEQUATE';

/**
 * 验证评估状态是否有效
 * 修复漏洞1: 评估状态处理缺陷
 */
export function validateAssessmentStatus(status: unknown, itemName: string): {
  isValid: boolean;
  status: AssessmentStatus | 'UNKNOWN';
  warning?: string;
} {
  const validStatuses: AssessmentStatus[] = [
    'PASS', 'FAIL', 'OK', 'NEEDS_ATTENTION', 'SUPPORTED', 'ADEQUATE', 'INADEQUATE'
  ];

  if (typeof status !== 'string' || !validStatuses.includes(status as AssessmentStatus)) {
    const warning = `未知的评估状态: ${status} (评估项: ${itemName})`;
    console.warn(warning);
    return {
      isValid: false,
      status: 'UNKNOWN',
      warning
    };
  }

  return {
    isValid: true,
    status: status as AssessmentStatus
  };
}

/**
 * 检查照护者评估数据完整性
 * 修复漏洞2: 照护者评估数据流缺陷
 */
export function validateCarerAssessmentData(
  hasCarer: boolean,
  carerSupportData: any,
  itemName: string = 'carerSupport'
): {
  isComplete: boolean;
  shouldInclude: boolean;
  recommendation?: string;
  warning?: string;
} {
  if (!hasCarer) {
    return {
      isComplete: true,
      shouldInclude: false
    };
  }

  if (!carerSupportData) {
    const warning = `用户有照护者但缺少照护者评估数据 (评估项: ${itemName})`;
    console.warn(warning);
    return {
      isComplete: false,
      shouldInclude: true,
      recommendation: '照护者评估数据缺失，建议完成照护者支持评估',
      warning
    };
  }

  return {
    isComplete: true,
    shouldInclude: true
  };
}

/**
 * 验证数字类型的评估数据
 * 修复漏洞3: 边界条件处理
 */
export function validateNumericData(
  value: unknown,
  fieldName: string,
  constraints: {
    min?: number;
    max?: number;
    defaultValue?: number;
    allowZero?: boolean;
  } = {}
): {
  isValid: boolean;
  validatedValue: number;
  originalValue: unknown;
  warning?: string;
} {
  const { min = 0, max = Number.MAX_SAFE_INTEGER, defaultValue = 0, allowZero = true } = constraints;
  
  // 处理 null, undefined, NaN 等情况
  if (value == null || Number.isNaN(Number(value))) {
    return {
      isValid: false,
      validatedValue: defaultValue,
      originalValue: value,
      warning: `${fieldName}: 无效数值 (${value})，使用默认值 ${defaultValue}`
    };
  }

  const numValue = Number(value);
  
  // 检查是否为零值（如果不允许）
  if (!allowZero && numValue === 0) {
    return {
      isValid: false,
      validatedValue: defaultValue,
      originalValue: value,
      warning: `${fieldName}: 不允许零值，使用默认值 ${defaultValue}`
    };
  }

  // 检查边界条件
  if (numValue < min) {
    return {
      isValid: false,
      validatedValue: min,
      originalValue: value,
      warning: `${fieldName}: 值 ${numValue} 小于最小值 ${min}，已修正为 ${min}`
    };
  }

  if (numValue > max) {
    return {
      isValid: false,
      validatedValue: max,
      originalValue: value,
      warning: `${fieldName}: 值 ${numValue} 大于最大值 ${max}，已修正为 ${max}`
    };
  }

  return {
    isValid: true,
    validatedValue: numValue,
    originalValue: value
  };
}

/**
 * 生成基于状态的建议类别
 * 修复建议生成逻辑的状态处理
 */
export function generateAdviceByStatus(status: AssessmentStatus, itemName: string): {
  category: 'pass' | 'fail' | 'unknown';
  advice: string;
} {
  const failStatuses: AssessmentStatus[] = ['FAIL', 'NEEDS_ATTENTION', 'INADEQUATE'];
  const passStatuses: AssessmentStatus[] = ['PASS', 'OK', 'SUPPORTED', 'ADEQUATE'];

  if (failStatuses.includes(status)) {
    return {
      category: 'fail',
      advice: `${itemName}需要关注和干预`
    };
  }

  if (passStatuses.includes(status)) {
    return {
      category: 'pass',
      advice: `${itemName}状态良好，建议维持`
    };
  }

  return {
    category: 'unknown',
    advice: `${itemName}评估状态异常，建议重新评估`
  };
}

/**
 * 安全的评估结果过滤器
 * 确保所有评估项都被正确处理，包括缺失的数据
 */
export function safeFilterAssessmentResults<T>(
  results: Record<string, T | undefined>,
  options: {
    includeUndefined?: boolean;
    customFilter?: (key: string, value: T | undefined) => boolean;
  } = {}
): Array<[string, T | undefined]> {
  const { includeUndefined = false, customFilter } = options;

  return Object.entries(results).filter(([key, value]) => {
    if (customFilter) {
      return customFilter(key, value);
    }
    
    return includeUndefined || value !== undefined;
  });
}

/**
 * 评估数据完整性检查
 * 提供整体的数据质量报告
 */
export function assessDataIntegrity(
  intrinsicResults: Record<string, any>,
  keyFactorsResults: Record<string, any>,
  userProfile: { hasCarer: boolean }
): {
  isComplete: boolean;
  issues: Array<{
    severity: 'warning' | 'error';
    category: string;
    message: string;
    suggestion: string;
  }>;
  summary: {
    totalAssessments: number;
    completeAssessments: number;
    missingAssessments: string[];
  };
} {
  const issues: Array<{
    severity: 'warning' | 'error';
    category: string;
    message: string;
    suggestion: string;
  }> = [];

  const expectedIntrinsic = ['cognition', 'mobility', 'vitality', 'vision', 'hearing', 'psychologicalCapacity'];
  const expectedKeyFactors = ['socialCareSupport', 'urinaryIncontinence'];
  if (userProfile.hasCarer) {
    expectedKeyFactors.push('carerSupport');
  }

  const allExpected = [...expectedIntrinsic, ...expectedKeyFactors];
  const allResults = { ...intrinsicResults, ...keyFactorsResults };
  
  const missingAssessments: string[] = [];
  
  allExpected.forEach(key => {
    if (!allResults[key]) {
      missingAssessments.push(key);
      
      const severity = (key === 'carerSupport' && userProfile.hasCarer) ? 'error' : 'warning';
      issues.push({
        severity,
        category: 'missing_data',
        message: `缺少 ${key} 评估数据`,
        suggestion: severity === 'error' ? '必须完成此评估' : '建议补充此评估数据'
      });
    }
  });

  return {
    isComplete: missingAssessments.length === 0,
    issues,
    summary: {
      totalAssessments: allExpected.length,
      completeAssessments: allExpected.length - missingAssessments.length,
      missingAssessments
    }
  };
}